import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { ArrowLeft, Loader2, CheckCircle2, Clock, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const fmtEur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const CATEGORIES = [
    { value: 'revision_entretien', label: 'Révision / entretien' },
    { value: 'mecanique', label: 'Mécanique' },
    { value: 'pneumatiques', label: 'Pneumatiques' },
    { value: 'carrosserie', label: 'Carrosserie' },
    { value: 'controle_technique', label: 'Contrôle technique' },
    { value: 'pieces', label: 'Pièces' },
    { value: 'diagnostic', label: 'Diagnostic' },
    { value: 'preparation', label: 'Préparation' },
    { value: 'nettoyage', label: 'Nettoyage' },
    { value: 'transport', label: 'Transport' },
    { value: 'autre', label: 'Autre' },
];

const STATUS_MSG = {
    rattache: { icon: CheckCircle2, text: 'Rattaché automatiquement', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
    en_attente: { icon: Clock, text: 'En attente de rattachement', color: 'text-amber-600 bg-amber-50 border-amber-200' },
    a_verifier: { icon: AlertTriangle, text: 'Plusieurs véhicules trouvés — à vérifier', color: 'text-red-600 bg-red-50 border-red-200' },
};

const EMPTY = {
    date_facture: new Date().toISOString().slice(0, 10),
    immatriculation: '', vehicule_modele: '', categorie: 'autre',
    fournisseur: '', numero_facture: '', montant_ttc: '',
    taux_tva: 20, tva_recuperable: false, description: '',
};

const JUSTIF = { visible: 'Justificatif rattaché et consultable', non_visible: 'Justificatif enregistré, pas encore visible dans le dossier véhicule', introuvable: 'Justificatif référencé mais fichier introuvable', en_attente: 'Justificatif enregistré, frais pas encore rattaché', aucun: 'Aucun justificatif pour ce frais' };

function LinkedInfo({ meta, vehicle, id }) {
    const open = async () => {
        const w = window.open('', '_blank');
        try {
            const r = await axios.get(`${API_URL}/api/frais-vehicules/${id}/document`, { withCredentials: true, responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            if (w) w.location.href = url; else window.location.href = url;
        } catch (e) { if (w) w.close(); toast.error('Justificatif introuvable'); }
    };
    return (
        <div className="card p-4 mb-6 space-y-2 text-sm" data-testid="fv-linked-info">
            <p className="text-gray-700" data-testid="fv-linked-vehicle">Véhicule associé : {vehicle ? <b>{vehicle.nom}{vehicle.modele && vehicle.modele.trim() !== (vehicle.nom || '').trim() ? ` — ${vehicle.modele}` : ''} ({vehicle.immatriculation})</b> : <span className="text-gray-400">aucun (frais non rattaché)</span>}</p>
            {meta.vehicule_modele && vehicle && <p className="text-xs text-gray-500">Modèle saisi ou lu sur la facture : « {meta.vehicule_modele} » — information de la facture, distincte du véhicule associé.</p>}
            <p className={`text-xs ${meta.justificatif === 'visible' ? 'text-emerald-700' : meta.justificatif === 'aucun' ? 'text-gray-500' : 'text-amber-700'}`} data-testid="fv-justif-status">{JUSTIF[meta.justificatif] || meta.justificatif}</p>
            {meta.has_document && <Button type="button" size="sm" variant="outline" onClick={open} className="h-8 text-xs" data-testid="fv-open-document">Voir le justificatif</Button>}
            {meta.ecart && <p className="text-xs font-semibold text-amber-700" data-testid="fv-form-ecart">Écart : facture {fmtEur(meta.ecart.montant_facture)} / dépense « {meta.ecart.depense} » {fmtEur(meta.ecart.montant_depense)} — à décider par l'administrateur, rien n'est modifié automatiquement.</p>}
        </div>
    );
}

export default function FraisVehiculeFormPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const [form, setForm] = useState({ ...EMPTY });
    const [saving, setSaving] = useState(false);
    const [keepFields, setKeepFields] = useState(true);
    const [lastResult, setLastResult] = useState(null);
    const [dupeWarning, setDupeWarning] = useState(null); // {fv_id, existing_frais, vehicle_nom, andNext}
    const immatRef = useRef(null);
    const [meta, setMeta] = useState(null);
    const [vehicle, setVehicle] = useState(null);

    // Load existing for edit mode
    useEffect(() => {
        if (!isEdit) return;
        axios.get(`${API_URL}/api/frais-vehicules`, { withCredentials: true })
            .then(res => {
                const item = res.data.find(d => d.id === id);
                if (item) {
                    setMeta(item);
                    if (item.vehicle_id) axios.get(`${API_URL}/api/vehicles/${item.vehicle_id}`, { withCredentials: true }).then(r => setVehicle(r.data)).catch(() => setVehicle(null));
                    setForm({
                        date_facture: item.date_facture || '', immatriculation: item.immatriculation || '',
                        vehicule_modele: item.vehicule_modele || '', categorie: item.categorie || 'autre',
                        fournisseur: item.fournisseur || '', numero_facture: item.numero_facture || '',
                        montant_ttc: item.montant_ttc || '', taux_tva: item.taux_tva || 20,
                        tva_recuperable: item.tva_recuperable || false, description: item.description || '',
                    });
                }
            });
    }, [id, isEdit]);

    // Auto-focus immat on mount
    useEffect(() => { if (!isEdit) setTimeout(() => immatRef.current?.focus(), 200); }, [isEdit]);

    const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const handleSave = async (andNext = false, forceFlag = false) => {
        if (!form.immatriculation.trim()) { toast.error('Immatriculation requise'); return; }
        if (!form.montant_ttc || parseFloat(form.montant_ttc) <= 0) { toast.error('Montant TTC requis'); return; }
        setSaving(true);
        setLastResult(null);
        try {
            const payload = { ...form, montant_ttc: parseFloat(form.montant_ttc), taux_tva: parseFloat(form.taux_tva), force: forceFlag };
            if (isEdit) {
                await axios.put(`${API_URL}/api/frais-vehicules/${id}`, payload, { withCredentials: true });
                toast.success('Frais modifié');
                navigate('/frais-vehicules');
            } else {
                const res = await axios.post(`${API_URL}/api/frais-vehicules`, payload, { withCredentials: true });

                // Potential duplicate detected — show warning, do NOT proceed
                if (res.data.potential_duplicate) {
                    setDupeWarning({
                        fv_id: res.data.id,
                        existing_frais: res.data.existing_frais || [],
                        vehicle_nom: res.data.vehicle_nom || '',
                        andNext,
                    });
                    setSaving(false);
                    return;
                }

                const st = res.data.statut;
                setLastResult({ statut: st, vehicle_nom: res.data.vehicle_nom || '' });
                if (st === 'rattache') toast.success(`Rattaché → ${res.data.vehicle_nom}`);
                else if (st === 'a_verifier') toast.warning('Plusieurs véhicules trouvés — à vérifier');
                else toast.success('Frais enregistré (en attente)');

                if (andNext) {
                    setForm(prev => ({
                        ...EMPTY,
                        date_facture: prev.date_facture,
                        fournisseur: keepFields ? prev.fournisseur : '',
                        taux_tva: keepFields ? prev.taux_tva : 20,
                        tva_recuperable: keepFields ? prev.tva_recuperable : false,
                    }));
                    setTimeout(() => immatRef.current?.focus(), 100);
                } else {
                    navigate('/frais-vehicules');
                }
            }
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
        setSaving(false);
    };

    const handleForceAttach = async () => {
        if (!dupeWarning) return;
        const fvId = dupeWarning.fv_id;
        const andNext = dupeWarning.andNext;
        setDupeWarning(null); // Close dialog FIRST (before async call, prevents onOpenChange race)
        setSaving(true);
        try {
            const res = await axios.put(`${API_URL}/api/frais-vehicules/${fvId}/force-attach`, {}, { withCredentials: true });
            const st = res.data.statut;
            setLastResult({ statut: st, vehicle_nom: res.data.vehicle_nom || '' });
            toast.success(`Rattaché → ${res.data.vehicle_nom || 'véhicule'}`);
            if (andNext) {
                setForm(prev => ({
                    ...EMPTY,
                    date_facture: prev.date_facture,
                    fournisseur: keepFields ? prev.fournisseur : '',
                    taux_tva: keepFields ? prev.taux_tva : 20,
                    tva_recuperable: keepFields ? prev.tva_recuperable : false,
                }));
                setTimeout(() => immatRef.current?.focus(), 100);
            } else {
                navigate('/frais-vehicules');
            }
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
        setSaving(false);
    };

    const handleReplaceFrais = async (fraisIdx) => {
        if (!dupeWarning) return;
        const fvId = dupeWarning.fv_id;
        const andNext = dupeWarning.andNext;
        const oldFrais = dupeWarning.existing_frais?.find(f => f.idx === fraisIdx);
        setDupeWarning(null);
        setSaving(true);
        try {
            const res = await axios.put(`${API_URL}/api/frais-vehicules/${fvId}/replace/${fraisIdx}`, {}, { withCredentials: true });
            const st = res.data.statut;
            setLastResult({ statut: st, vehicle_nom: res.data.vehicle_nom || '' });
            toast.success(`Frais remplacé (${oldFrais?.montant_ttc?.toFixed(2) || '?'} € → ${res.data.replaced?.new_montant?.toFixed(2) || '?'} €) → ${res.data.vehicle_nom || 'véhicule'}`);
            if (andNext) {
                setForm(prev => ({
                    ...EMPTY,
                    date_facture: prev.date_facture,
                    fournisseur: keepFields ? prev.fournisseur : '',
                    taux_tva: keepFields ? prev.taux_tva : 20,
                    tva_recuperable: keepFields ? prev.tva_recuperable : false,
                }));
                setTimeout(() => immatRef.current?.focus(), 100);
            } else {
                navigate('/frais-vehicules');
            }
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur lors du remplacement'); }
        setSaving(false);
    };

    const handleCancelDupe = () => {
        if (!dupeWarning) return;
        const fvId = dupeWarning.fv_id;
        setDupeWarning(null);
        if (fvId) {
            axios.delete(`${API_URL}/api/frais-vehicules/${fvId}`, { withCredentials: true }).catch(() => {});
        }
        toast.info('Frais annulé');
    };

    const handleKeyDown = (e) => {
        if (e.ctrlKey && e.key === 'Enter') { e.preventDefault(); handleSave(true); }
    };

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" onKeyDown={handleKeyDown} data-testid="fv-form-page">
            {/* Header */}
            <button onClick={() => navigate('/frais-vehicules')} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors mb-6" data-testid="fv-form-back">
                <ArrowLeft className="w-4 h-4" />Retour aux frais véhicules
            </button>
            <h1 className="text-xl font-semibold text-gray-900 tracking-tight mb-8">
                {isEdit ? 'Modifier le frais véhicule' : 'Ajouter un frais véhicule'}
            </h1>

            {/* Last result feedback */}
            {lastResult && (() => {
                const cfg = STATUS_MSG[lastResult.statut] || STATUS_MSG.en_attente;
                const Icon = cfg.icon;
                return (
                    <div className={`flex items-center gap-2.5 px-4 py-3 rounded-lg border mb-6 text-sm ${cfg.color}`} data-testid="fv-last-result">
                        <Icon className="w-4 h-4 shrink-0" />
                        <span>{cfg.text}{lastResult.vehicle_nom ? ` — ${lastResult.vehicle_nom}` : ''}</span>
                    </div>
                );
            })()}

            {isEdit && meta && <LinkedInfo meta={meta} vehicle={vehicle} id={id} />}

            {/* Form — 2 columns desktop, 1 column mobile */}
            <div className="card p-6 sm:p-8 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                    <Field label="Date facture">
                        <Input type="date" value={form.date_facture} onChange={e => set('date_facture', e.target.value)} data-testid="fv-date" />
                    </Field>
                    <Field label="Immatriculation">
                        <Input ref={immatRef} value={form.immatriculation} onChange={e => set('immatriculation', e.target.value.toUpperCase())} placeholder="AB-123-CD" className="font-mono tracking-wider" data-testid="fv-immat" />
                    </Field>
                    <Field label="Véhicule / modèle">
                        <Input value={form.vehicule_modele} onChange={e => set('vehicule_modele', e.target.value)} placeholder="Modèle indiqué sur la facture (facultatif)" data-testid="fv-modele" />
                    </Field>
                    <Field label="Catégorie">
                        <Select value={form.categorie} onValueChange={v => set('categorie', v)}>
                            <SelectTrigger className="h-10 text-sm" data-testid="fv-categorie"><SelectValue /></SelectTrigger>
                            <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </Field>
                    <Field label="Fournisseur">
                        <Input value={form.fournisseur} onChange={e => set('fournisseur', e.target.value)} placeholder="Optionnel" data-testid="fv-fournisseur" />
                    </Field>
                    <Field label="N° facture">
                        <Input value={form.numero_facture} onChange={e => set('numero_facture', e.target.value)} placeholder="Optionnel" data-testid="fv-num-facture" />
                    </Field>
                    <Field label="Montant TTC (€)">
                        <Input type="number" step="0.01" inputMode="decimal" value={form.montant_ttc} onChange={e => set('montant_ttc', e.target.value)} placeholder="320.00" className="font-mono" data-testid="fv-montant" />
                    </Field>
                    <Field label="Taux TVA (%)">
                        <Input type="number" value={form.taux_tva} onChange={e => set('taux_tva', parseFloat(e.target.value) || 20)} data-testid="fv-taux-tva" />
                    </Field>
                    <Field label="TVA récupérable">
                        <Select value={form.tva_recuperable ? 'oui' : 'non'} onValueChange={v => set('tva_recuperable', v === 'oui')}>
                            <SelectTrigger className="h-10 text-sm" data-testid="fv-tva-recup"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="oui">Oui</SelectItem><SelectItem value="non">Non</SelectItem></SelectContent>
                        </Select>
                    </Field>
                    <Field label="Description">
                        <Input value={form.description} onChange={e => set('description', e.target.value)} placeholder="Commentaire optionnel" data-testid="fv-description" />
                    </Field>
                </div>

                {!isEdit && (
                    <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer pt-2">
                        <input type="checkbox" checked={keepFields} onChange={e => setKeepFields(e.target.checked)} className="rounded border-gray-300 text-[#6366F1] focus:ring-[#6366F1] w-3.5 h-3.5" />
                        Conserver date, fournisseur et TVA entre les saisies
                    </label>
                )}
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => navigate('/frais-vehicules')} className="h-10 px-5 text-sm">Annuler</Button>
                {!isEdit && (
                    <Button variant="outline" onClick={() => handleSave(true)} disabled={saving}
                        className="h-10 px-5 text-sm text-[#6366F1] border-[#6366F1]/30 hover:bg-[#6366F1]/5" data-testid="save-and-next-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Enregistrer + suivant
                    </Button>
                )}
                <Button onClick={() => handleSave(false)} disabled={saving}
                    className="h-10 px-6 text-sm bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid="save-fv-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Enregistrer
                </Button>
            </div>

            <p className="text-[11px] text-gray-300 text-center mt-4">Ctrl + Entrée = Enregistrer + suivant</p>

            {/* Duplicate Warning Dialog */}
            <AlertDialog open={!!dupeWarning} onOpenChange={() => { /* only Cancel button handles dismiss */ }}>
                <AlertDialogContent className="max-w-lg" data-testid="dupe-warning-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                            <AlertTriangle className="w-5 h-5" />Frais potentiellement déjà enregistré
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3">
                                <p className="text-sm text-gray-600">
                                    Vous ajoutez <strong>{parseFloat(form.montant_ttc).toFixed(2)} € TTC</strong> sur le véhicule <strong>{dupeWarning?.vehicle_nom}</strong>.
                                    {dupeWarning?.existing_frais?.some(f => f.reason === 'similar')
                                        ? " Un frais similaire (montant proche) existe déjà — il s'agit peut-être d'une estimation à remplacer."
                                        : " Un frais du même montant existe déjà."}
                                </p>
                                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Frais existants :</p>
                                    {dupeWarning?.existing_frais?.map((f, i) => (
                                        <div key={i} className="flex items-center justify-between gap-2 text-sm py-1 border-b border-gray-100 last:border-0">
                                            <div className="flex-1 min-w-0">
                                                <span className="text-gray-700 truncate block">{f.nom || 'Frais'}</span>
                                                <span className="text-[10px] text-gray-400">
                                                    {f.reason === 'similar' ? 'Montant similaire' : 'Montant identique'}
                                                </span>
                                            </div>
                                            <span className="font-mono text-gray-900 font-medium shrink-0">{f.montant_ttc?.toFixed(2)} €</span>
                                            <button
                                                type="button"
                                                onClick={() => handleReplaceFrais(f.idx)}
                                                className="shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                                                data-testid={`dupe-replace-btn-${i}`}
                                            >
                                                <RefreshCw className="w-3 h-3" />Remplacer
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-400">
                                    <strong>Remplacer</strong> écrase l'ancien frais. <strong>Enregistrer quand même</strong> ajoute un 2ème frais distinct.
                                </p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                        <AlertDialogCancel onClick={handleCancelDupe} data-testid="dupe-cancel-btn">Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleForceAttach} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="dupe-force-btn">
                            Enregistrer quand même
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs text-gray-400 uppercase tracking-wider font-medium">{label}</Label>
            {children}
        </div>
    );
}
